import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { CHECKIN_METHODS } from '../../utils/checkInMethods';

export const PRIMARY_BTN = 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md shadow-violet-500/20 hover:from-violet-500 hover:to-fuchsia-500';

const NAV = [
  ['/#features', 'Features'],
  ['/#check-in', 'Check-in'],
  ['/#solutions', 'Solutions'],
  ['/#how-it-works', 'How it works'],
  ['/#faq', 'FAQ'],
];

/** "Book a demo" link; `from` records which page the enquiry came from. */
export const demoLink = (from) => (from ? `/demo?from=${encodeURIComponent(from)}` : '/demo');

export const SiteHeader = ({ demoFrom }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 border-b bg-white/95 backdrop-blur transition-shadow ${scrolled ? 'shadow-sm' : ''}`}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-[72px] sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center" aria-label="EventEase home">
          <img src="/logo-header.png" alt="EventEase" className="h-9 w-auto sm:h-11" width="146" height="44" />
        </Link>
        <nav className="hidden items-center gap-7 text-sm font-medium text-gray-600 lg:flex" aria-label="Main">
          {NAV.map(([href, label]) => <a key={href} href={href} className="hover:text-violet-700">{label}</a>)}
          <Link to="/events" className="hover:text-violet-700">Events</Link>
          <Link to="/gallery" className="hover:text-violet-700">Gallery</Link>
        </nav>
        <div className="hidden lg:block">
          <Button asChild className={PRIMARY_BTN}><Link to={demoLink(demoFrom)}>Book a demo</Link></Button>
        </div>
        <button type="button" className="rounded-md p-2 text-gray-700 lg:hidden" onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen}>
          {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {menuOpen && (
        <div className="border-t bg-white px-4 pb-4 shadow-lg lg:hidden">
          <nav className="flex flex-col py-2 text-gray-700" aria-label="Mobile">
            {NAV.map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)} className="rounded px-2 py-2.5 hover:bg-violet-50">{label}</a>
            ))}
            <Link to="/events" className="rounded px-2 py-2.5 hover:bg-violet-50">Events</Link>
            <Link to="/gallery" className="rounded px-2 py-2.5 hover:bg-violet-50">Gallery</Link>
          </nav>
          <Button asChild className={`w-full ${PRIMARY_BTN}`}><Link to={demoLink(demoFrom)}>Book a demo</Link></Button>
        </div>
      )}
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="bg-[#0f0720] py-12 text-slate-300">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <span className="inline-block rounded-lg bg-white px-3 py-2"><img src="/logo-header.png" alt="EventEase" className="h-9 w-auto" /></span>
          <p className="mt-4 max-w-sm text-sm text-slate-400">
            Event registration, payments and on-site check-in by NextWave SaaS OPC Private Limited, New Delhi.
          </p>
          <div className="mt-4 flex flex-col gap-2 text-sm">
            <a href="mailto:info@nextwavesaas.tech" className="hover:text-white">info@nextwavesaas.tech</a>
            <Link to="/demo" className="hover:text-white">Book a demo</Link>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Product</h3>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a href="/#features" className="hover:text-white">Features</a></li>
            <li><Link to="/events" className="hover:text-white">Upcoming events</Link></li>
            <li><Link to="/gallery" className="hover:text-white">Gallery</Link></li>
            <li><Link to="/my-registration" className="hover:text-white">Find my registration</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Check-in</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {CHECKIN_METHODS.map((m) => (
              <li key={m.slug}><Link to={`/check-in/${m.slug}`} className="hover:text-white">{m.title}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Company</h3>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link to="/about-us" className="hover:text-white">About us</Link></li>
            <li><Link to="/contact-us" className="hover:text-white">Contact support</Link></li>
            <li><Link to="/testimonial" className="hover:text-white">Share feedback</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Legal</h3>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link to="/privacy-policy" className="hover:text-white">Privacy policy</Link></li>
            <li><Link to="/terms-of-service" className="hover:text-white">Terms of service</Link></li>
            <li><Link to="/refund-policy" className="hover:text-white">Refund &amp; cancellation</Link></li>
            <li><Link to="/shipping-policy" className="hover:text-white">Shipping &amp; delivery</Link></li>
          </ul>
        </div>
      </div>
      <div className="mt-10 border-t border-white/10 pt-6 text-sm text-slate-500">
        &copy; {new Date().getFullYear()} NextWave SaaS OPC Private Limited. All rights reserved.
      </div>
    </div>
  </footer>
);
